import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronRight, Pencil, Plus } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { OverviewToolbar } from "../../components/layout/OverviewToolbar";
import { Modal } from "../../components/ui/Modal";
import { managersAdminApi, type ManagerAdmin } from "../../api/managersAdminApi";

/**
 * Admin overview of managers (whitelabel brands): name, portal, customers
 * and logins, with a "New manager" action. Details, brand and logins are
 * edited on the manager page.
 */
export function ManagersAdminPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ManagerAdmin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await managersAdminApi.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load managers");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = items ?? [];
    if (!q) return all;
    return all.filter((m) => m.name.toLowerCase().includes(q) || m.portalName.toLowerCase().includes(q) || m.portalUrl.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <>
      <PageHeader
        title="Managers"
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Plus size={14} /> New manager
          </button>
        }
      />
      <div className="dashboard">
        <OverviewToolbar search={search} onSearch={setSearch} placeholder="Search by name or portal…" count={{ matched: filtered.length, total: items?.length ?? 0 }} />
        {error && (
          <div className="tile" style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}>{error}</div>
        )}
        {items == null && !error ? (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading managers…</div>
        ) : filtered.length === 0 ? (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>No managers found.</div>
        ) : (
          <div className="tile-rows">
            {filtered.map((m) => (
              <button
                key={m.managerId}
                type="button"
                className="tile tile-link tile-row"
                onClick={() => navigate(`/admin/managers/${m.managerId}`)}
                style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 16, padding: "12px 18px", textAlign: "left" }}
                aria-label={`Open ${m.name}`}
              >
                <span
                  aria-hidden
                  style={{ width: 40, height: 40, borderRadius: 10, background: m.primaryColor ? `${m.primaryColor}22` : "var(--portal-primary-soft)", display: "grid", placeItems: "center", flexShrink: 0, overflow: "hidden" }}
                >
                  {m.hasLogo ? (
                    <img src={`/api/brand/logo?managerId=${m.managerId}`} alt="" style={{ maxWidth: 32, maxHeight: 32, objectFit: "contain" }} />
                  ) : (
                    <Building2 size={18} style={{ color: m.primaryColor ?? "var(--portal-primary-darker)" }} />
                  )}
                </span>
                <div className="tile-row-title" style={{ flex: "2 1 0", minWidth: 0, fontWeight: 600, fontSize: "1rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.name}>
                  {m.name || "—"}
                </div>
                <div className="tile-row-detail" style={{ flex: "2 1 0", minWidth: 0, color: "var(--portal-muted)", fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.portalName || "—"}{m.portalUrl ? ` · ${m.portalUrl}` : ""}
                </div>
                <div className="tile-row-detail" style={{ flex: "1 1 0", minWidth: 0, color: "var(--portal-muted)", fontSize: "0.9rem", whiteSpace: "nowrap" }}>
                  {m.customerCount} customers · {m.loginCount} logins
                </div>
                <span className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem", flexShrink: 0 }}>
                  <Pencil size={14} /> Edit
                </span>
                <ChevronRight size={16} style={{ color: "var(--portal-primary)", flexShrink: 0 }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {createOpen && (
        <CreateManagerModal
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            navigate(`/admin/managers/${id}`);
          }}
        />
      )}
    </>
  );
}

function CreateManagerModal({ onClose, onCreated }: { onClose: () => void; onCreated: (managerId: number) => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await managersAdminApi.create(name.trim());
      onCreated(r.managerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create manager");
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => !busy && onClose()}
      title="New manager"
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void create()} disabled={busy}>{busy ? "Creating…" : "Create"}</button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void create(); }} style={{ display: "grid", gap: 8 }}>
        {error && <div style={{ color: "#c0392b", fontSize: "0.9rem" }}>{error}</div>}
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Name</span>
          <input className="portal-input-inline" style={{ width: "100%", padding: "8px 10px", border: "2px solid var(--portal-border)", borderRadius: 8, fontFamily: "inherit", fontSize: "0.9rem" }} value={name} maxLength={50} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>Portal name, URL, mail sender, colour, logo and logins are set on the manager page afterwards.</p>
      </form>
    </Modal>
  );
}
